import React from 'react';
import BorderContainer from '../shared/container/BorderContainer';
import PrimaryButton from '../shared/button/PrimaryButton';
import { CaptionText, MinorHeading } from '../shared/typography/PrimaryTypography';
import { Box, IconButton } from '@mui/material';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Company, Group, PBIReport, User } from '@esg/esg-global-types';
import { UserContext } from '../../context/UserContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import PrimarySkeleton from '../shared/loading/PrimarySkeleton';

export interface ViewsWidgetConfig {
  title: string;
  description: string;
  image: string;
  link: string;
  module_id: string;
  view_id: string;
  valid_user_access: boolean;
  image_bottom?: boolean;
  align_left?: boolean;
}

interface ViewsWidgetProps {
  views: Array<ViewsWidgetConfig>;
  pbi_reports: Array<PBIReport> | null;
  handleDisplayAccessDeniedModal: () => void;
}

/**
 * Display card of one or multiple module views for home page with dynamic content
 * @param {string} title The main heading text of the current view.
 * @param {string} description The caption text describing the current view in the widget.
 * @param {string} image The image shown representing the view.
 * @param {string} link Navigation link to view on CLICK HERE button.
 * @param {string} module_id ID of view's module to be used for permissions checking.
 * @param {string} view_id ID of view to be used for permission checking and PowerBI Report check.
 * @param {boolean | undefined} image_bottom Optionally render the image below the title and description text.
 * @param {boolean | undefined} align_left Optionally align header and description text to the left of the widget.
 * @param {Array<PBIReport> | null} pbi_reports List of configured PowerBI Reports for company, used to validate pbi requiring views, null if still loading in parent.
 * @param {void} handleDisplayAccessDeniedModal Display the module access denied modal in parent.
 * @returns {JSX.Element}
 */
const ViewWidgets = ({ views, pbi_reports, handleDisplayAccessDeniedModal }: ViewsWidgetProps) => {
  const { group }: { group: Group | null } = React.useContext(GroupContext);
  const { company }: { company: Company | null } = React.useContext(CompanyContext);
  const { user }: { user: User | null } = React.useContext(UserContext);
  const [currentViewIndex, setCurrentViewIndex] = React.useState<number>(0);

  const widget_loading: boolean = user === null || pbi_reports === null;

  const navigate: NavigateFunction = useNavigate();

  // Current view being displayed in widget, to be rotated if more than 1 provided to widget.
  const current_widget_view: ViewsWidgetConfig | undefined = views[currentViewIndex];

  const handleViewNavigate = (): void => {
    if (user && group && company) {
      if (!current_widget_view.valid_user_access) {
        handleDisplayAccessDeniedModal();
        return;
      }
      navigate(current_widget_view.link);
    }
  };

  const handlePrevView = (): void => {
    setCurrentViewIndex(currentViewIndex === 0 ? views.length - 1 : currentViewIndex - 1);
  };

  const handleNextView = (): void => {
    setCurrentViewIndex(currentViewIndex === views.length - 1 ? 0 : currentViewIndex + 1);
  };

  if (!current_widget_view) return null;

  return widget_loading ? (
    <PrimarySkeleton variant="rounded" height={250} sx={{ borderRadius: '24px' }} />
  ) : (
    <BorderContainer sx={{ height: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          gap: 2,
          boxSizing: 'border-box',
          py: 1,
          justifyContent: 'center'
        }}
      >
        {views.length > 1 && (
          <IconButton onClick={handlePrevView} sx={{ m: 'auto' }}>
            <ArrowBackIosNewIcon color="secondary" fontSize="large" />
          </IconButton>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '90%',
            height: '100%',
            gap: 2,
            boxSizing: 'border-box',
            alignItems: current_widget_view.align_left ? 'flex-start' : 'center',
            textAlign: current_widget_view.align_left ? 'start' : 'center',
            justifyContent: 'space-between'
          }}
        >
          {!current_widget_view.image_bottom && (
            <img
              style={{
                maxHeight: 110,
                maxWidth: '100%'
              }}
              src={current_widget_view.image}
              alt={current_widget_view.title}
            />
          )}
          <MinorHeading weight="Bold">{current_widget_view.title}</MinorHeading>
          <CaptionText weight="Medium">{current_widget_view.description}</CaptionText>
          {current_widget_view.image_bottom && (
            <img
              style={{
                maxHeight: 110,
                maxWidth: '100%'
              }}
              src={current_widget_view.image}
              alt={current_widget_view.title}
            />
          )}
          <PrimaryButton sx={{ mb: 1, width: '100%' }} onClick={handleViewNavigate}>
            CLICK HERE
          </PrimaryButton>
        </Box>
        {views.length > 1 && (
          <IconButton onClick={handleNextView} sx={{ m: 'auto' }}>
            <ArrowForwardIosIcon color="secondary" fontSize="large" />
          </IconButton>
        )}
      </Box>
    </BorderContainer>
  );
};

export default ViewWidgets;
